(() => {
	const STORAGE_KEY = 'c2g:favorites:v1';
	const selectors = {
		button: '[data-c2g-favorite-button]',
		count: '[data-c2g-favorites-count]',
		page: '[data-c2g-favorites-page]',
		grid: '[data-c2g-favorites-grid]',
		status: '[data-c2g-favorites-status]'
	};

	const readFavorites = () => {
		try {
			const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
			return Array.isArray(value) ? value.filter((item) => item && item.id && item.handle) : [];
		} catch (error) {
			return [];
		}
	};

	const writeFavorites = (favorites) => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites));
		window.dispatchEvent(new CustomEvent('c2g:favorites:change', { detail: favorites }));
	};

	const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
		style: 'currency',
		currency: window.Shopify?.currency?.active || 'USD'
	}).format((Number(cents) || 0) / 100);

	const escapeHtml = (value) => String(value || '')
		.replaceAll('&', '&amp;')
		.replaceAll('<', '&lt;')
		.replaceAll('>', '&gt;')
		.replaceAll('"', '&quot;')
		.replaceAll("'", '&#039;');

	const updateButtons = (favorites = readFavorites()) => {
		const ids = new Set(favorites.map((item) => String(item.id)));
		document.querySelectorAll(selectors.button).forEach((button) => {
			const saved = ids.has(String(button.dataset.productId));
			button.setAttribute('aria-pressed', String(saved));
			button.setAttribute('aria-label', `${saved ? 'Remove' : 'Save'} ${button.dataset.productTitle || 'product'} ${saved ? 'from' : 'to'} favorites`);
			button.title = saved ? 'Remove from favorites' : 'Save to favorites';
		});
	};

	const updateCounts = (favorites = readFavorites()) => {
		document.querySelectorAll(selectors.count).forEach((counter) => {
			counter.textContent = favorites.length;
			counter.hidden = favorites.length === 0;
		});
	};

	const syncInterface = (favorites = readFavorites()) => {
		updateButtons(favorites);
		updateCounts(favorites);
	};

	const toggleFavorite = (button) => {
		const id = String(button.dataset.productId);
		const favorites = readFavorites();
		const existingIndex = favorites.findIndex((item) => String(item.id) === id);

		if (existingIndex >= 0) {
			favorites.splice(existingIndex, 1);
		} else {
			favorites.unshift({
				id,
				handle: button.dataset.productHandle,
				title: button.dataset.productTitle,
				url: button.dataset.productUrl,
				image: button.dataset.productImage,
				price: Number(button.dataset.productPrice) || 0,
				savedAt: new Date().toISOString()
			});
		}

		writeFavorites(favorites);
		syncInterface(favorites);
	};

	const productImage = (product, fallback) => {
		const featured = product.featured_image || product.images?.[0] || fallback || '';
		if (typeof featured === 'string') return featured;
		return featured?.src || fallback || '';
	};

	const renderFavoriteCard = (saved, product) => {
		const variant = product?.variants?.find((item) => item.available) || product?.variants?.[0];
		const available = Boolean(product?.available && variant?.available);
		const title = product?.title || saved.title;
		const handle = product?.handle || saved.handle;
		const url = product?.url || `/products/${encodeURIComponent(handle)}`;
		const image = productImage(product, saved.image);
		const price = variant?.price ?? product?.price ?? saved.price;

		return `
			<article class="c2g-favorite-card" data-c2g-favorite-card="${escapeHtml(saved.id)}">
				<button type="button" class="c2g-favorite-card__remove" data-c2g-remove-favorite="${escapeHtml(saved.id)}" aria-label="Remove ${escapeHtml(title)} from favorites" title="Remove from favorites">&times;</button>
				<a class="c2g-favorite-card__image" href="${escapeHtml(url)}">
					${image ? `<img src="${escapeHtml(image)}" alt="${escapeHtml(title)}" loading="lazy">` : ''}
				</a>
				<div class="c2g-favorite-card__body">
					<p class="c2g-favorite-card__availability c2g-favorite-card__availability--${available ? 'in' : 'out'}">${available ? 'In stock' : 'Currently unavailable'}</p>
					<h2><a href="${escapeHtml(url)}">${escapeHtml(title)}</a></h2>
					<p class="c2g-favorite-card__price">${money(price)}</p>
					<a class="c2g-favorite-card__cta" href="${escapeHtml(url)}">View product</a>
				</div>
			</article>`;
	};

	const renderFavoritesPage = async () => {
		const page = document.querySelector(selectors.page);
		if (!page) return;

		const grid = page.querySelector(selectors.grid);
		const status = page.querySelector(selectors.status);
		const favorites = readFavorites();

		if (favorites.length === 0) {
			status.innerHTML = 'No favorites yet. Use the heart button on any product to save it here.';
			grid.replaceChildren();
			return;
		}

		status.textContent = `Updating ${favorites.length} saved ${favorites.length === 1 ? 'product' : 'products'}...`;
		const products = await Promise.all(favorites.map(async (saved) => {
			try {
				const response = await fetch(`/products/${encodeURIComponent(saved.handle)}.js`, { credentials: 'same-origin' });
				if (!response.ok) return null;
				return await response.json();
			} catch (error) {
				return null;
			}
		}));

		grid.innerHTML = favorites.map((saved, index) => renderFavoriteCard(saved, products[index])).join('');
		status.textContent = `${favorites.length} saved ${favorites.length === 1 ? 'product' : 'products'}`;
	};

	document.addEventListener('click', (event) => {
		const button = event.target.closest(selectors.button);
		if (button) {
			event.preventDefault();
			event.stopPropagation();
			toggleFavorite(button);
			return;
		}

		const removeButton = event.target.closest('[data-c2g-remove-favorite]');
		if (removeButton) {
			const id = String(removeButton.dataset.c2gRemoveFavorite);
			const favorites = readFavorites().filter((item) => String(item.id) !== id);
			writeFavorites(favorites);
			syncInterface(favorites);
			renderFavoritesPage();
		}
	}, true);

	document.addEventListener('DOMContentLoaded', () => {
		syncInterface();
		renderFavoritesPage();
	});
	window.addEventListener('c2g:favorites:change', (event) => syncInterface(event.detail));
	window.addEventListener('storage', () => {
		syncInterface();
		renderFavoritesPage();
	});
})();
